import React, { ReactElement } from 'react';
import { Box, Typography, ListItem, ListItemIcon, ListItemText, Divider, Alert } from '@material-ui/core';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { formatNumber } from '../../utils';

interface NavigationButtonProps {
  icon: ReactElement;
  title: string;
  details?: string;
  notification?: { text: string };
  indicator?: JSX.Element | JSX.Element[];
  count?: number;
  page: string;
  uid?: string;
  view: string;
  dividers?: {
    top?: boolean;
    bottom?: boolean;
  };
  testId?: string;
  children?: React.ReactNode;
}

const NavigationButton: React.FC<NavigationButtonProps> = ({
  icon,
  title,
  details,
  notification,
  count,
  page,
  uid,
  view,
  dividers,
  testId,
  indicator,
  children,
}: NavigationButtonProps) => {
  const path = `/app/${page}${uid ? `/${uid}` : ''}${view ? `/${view}` : ''}`;

  const history = useHistory();
  const isSelected = !!useRouteMatch({ path, exact: true });

  const handleClick = () => {
    window.scrollTo({ top: 0 });
    history.push(path);
  };

  const dataTestId = `navigation-button${page ? `-${page}` : ''}${view ? `-${view}` : ''}`;

  return (
    <>
      {dividers?.top && <Divider />}
      <ListItem button onClick={() => handleClick()} selected={isSelected} data-testid={dataTestId}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gridGap: '4px',
            gridTemplateRows: details && '1fr 1fr',
            alignItems: 'center',
          }}
          data-testid={testId}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText sx={{ mb: details ? 0 : 1 }}>
            <Box sx={{ display: indicator ? 'flex' : undefined, alignItems: 'center' }}>
              <Typography>{`${title}${count ? ` (${formatNumber(count)})` : ''}`}</Typography>
              {!!indicator && indicator}
            </Box>
          </ListItemText>
          {details && (
            <ListItemText sx={{ gridColumn: '2/3', mt: 0 }} data-testid={`${dataTestId}-details`}>
              <Typography
                variant="subtitle2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: 'text.secondary',
                }}
              >
                {details}
              </Typography>
            </ListItemText>
          )}
        </Box>
      </ListItem>
      {notification && (
        <Alert severity="error" data-testid={`${dataTestId}-notification`}>
          {notification.text}
        </Alert>
      )}
      {children && <Box px={4}>{children}</Box>}
      {dividers?.bottom && <Divider />}
    </>
  );
};

export default NavigationButton;
